import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocaleLanguages, getLocaleByCode } from "./utils";
import i18n from "../src/i18n";
import { AppLayout } from "./AppLayout";
import { SnackbarProvider } from "./shared/components/snackbar/Snackbar";
import { UppState } from "./App";
import getEnvironment from "./config/environment";
import { useEffect, useState } from "react";
import ErrorBoundary from "./layout/ErrorBoundary";
import {
  getCustomerIdFromToken,
  getUserIdFromToken,
} from "./utils/encrypt-decrypt-utilities";
import { useDispatch } from "react-redux";
import {
  updateCurrentCustomerId,
  updateCurrentUserId,
} from "./store/NotificationsSlice";

const envName = getEnvironment();

if (envName.name == "development") {
  require("./__mocks__");
}

interface AppWrapperProps {
  isUpp?: boolean;
  uppState?: UppState;
  pushHistoryAction?: (path: string) => void;
  clearKycRedirectAction: () => void;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
    },
  },
});

export const AppWrapper: React.FC<AppWrapperProps> = ({
  isUpp,
  uppState,
  pushHistoryAction,
  clearKycRedirectAction
}: AppWrapperProps) => {
  const [, setError] = useState<boolean>(false);
  const accessToken = localStorage.getItem("accessToken");

  const dispatch = useDispatch();

  useEffect(() => {
    const handleError = (e: ErrorEvent) => {
      console.warn("Error while loading the App in app wrapper", e);
      if (
        e.message &&
        (e.message.toLowerCase().includes("cannot read property") ||
          e.message.toLowerCase().includes("ScriptExternalLoadError"))
      ) {
        setError(true);
      }
    };

    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("error", handleError);
    };
  }, []);

  useEffect(() => {
    if (!accessToken) return;

    const userId = getUserIdFromToken(accessToken);
    const customerId = getCustomerIdFromToken(accessToken);

    if (!userId || !customerId) return;

    dispatch(updateCurrentCustomerId(Number(customerId)));
    dispatch(updateCurrentUserId(Number(userId)));
  }, [dispatch, accessToken]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={getLocaleByCode(i18n.language as LocaleLanguages)}
          >
            <SnackbarProvider>
              <AppLayout
                isUpp={isUpp}
                uppState={uppState}
                pushHistoryAction={pushHistoryAction}
                clearKycRedirectAction={clearKycRedirectAction}
              />
            </SnackbarProvider>
          </LocalizationProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};
